import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type {
  CcpPlan,
  FieldUpdatePayload,
  FieldUpdateSuggestion,
  PlanGenerateInput,
} from "../types"

import { generateCcp, getCcp, patchCcpField, requestCcpFieldUpdate } from "../api/ccp-api"
import { normalizeCcpPlan, resolveCcpCurrent } from "../plan-normalize"

export const ccpQueryKey = (segmentId: string, companyId: string) =>
  ["sa-ccp", segmentId, companyId] as const

function isNotFound(err: unknown): boolean {
  return (err as { response?: { status?: number } }).response?.status === 404
}

/** GET returns 404 before the first generate — surface that as `null` (empty state). */
async function getCcpOrNull(segmentId: string, companyId: string): Promise<CcpPlan | null> {
  try {
    return normalizeCcpPlan(await getCcp(segmentId, companyId))
  } catch (err) {
    if (isNotFound(err)) return null
    throw err
  }
}

export function useCcp(segmentId: string, companyId: string) {
  const queryClient = useQueryClient()
  const key = ccpQueryKey(segmentId, companyId)

  const planQuery = useQuery({
    queryKey: key,
    queryFn: () => getCcpOrNull(segmentId, companyId),
    enabled: Boolean(segmentId && companyId),
  })

  const generateMutation = useMutation({
    mutationFn: async (input: PlanGenerateInput = {}) =>
      normalizeCcpPlan(await generateCcp(segmentId, companyId, input)),
    onSuccess: (plan) => {
      queryClient.setQueryData(key, plan)
      // Refresh Customers-card summary blurbs after generate.
      // Card blurbs now ride along with the company list, so that is the key
      // to invalidate — TanStack matches key prefixes exactly.
      void queryClient.invalidateQueries({ queryKey: ["sa-ccp-summary-companies", segmentId] })
    },
  })

  /** Scoped AI suggestion — does NOT persist. Feed the result into the review modal. */
  const requestUpdate = useMutation({
    mutationFn: async (fieldKey: string): Promise<FieldUpdateSuggestion> => {
      // Scope the suggestion to the same area the plan was generated for —
      // the backend threads `area` into evidence resolution, and this call
      // used to post an empty body so it was always undefined.
      const area = queryClient.getQueryData<CcpPlan>(key)?.meta.area
      const res = await requestCcpFieldUpdate(segmentId, companyId, {
        fieldKey,
        input: area ? { area } : {},
      })
      // Fill `current` from the cached plan if an older backend omits it.
      if (res.current == null) {
        return {
          ...res,
          current: resolveCcpCurrent(queryClient.getQueryData<CcpPlan>(key), fieldKey),
        }
      }
      return res
    },
  })

  /** Persist an Accept / manual Save for a single field. */
  const patchField = useMutation({
    mutationFn: async (edit: { fieldKey: string; payload: FieldUpdatePayload }) =>
      normalizeCcpPlan(await patchCcpField(segmentId, companyId, edit)),
    onSuccess: (plan) => {
      queryClient.setQueryData(key, plan)
      // Card blurbs now ride along with the company list, so that is the key
      // to invalidate — TanStack matches key prefixes exactly.
      void queryClient.invalidateQueries({ queryKey: ["sa-ccp-summary-companies", segmentId] })
    },
  })

  return { planQuery, generateMutation, requestUpdate, patchField }
}
